import React from "react";
import {
  StyleSheet,
  View,
  Switch,
  TouchableOpacity,
  type StyleProp,
  type ViewStyle,
  type TextStyle,
  type TouchableOpacityProps,
} from "react-native";
import { ThemedText } from "./ThemedText";
import { useThemeColor } from "@/hooks/useThemeColor";
import { Spacing } from "@/constants/ThemeColours";

/**
 * Interface for ThemedToggle props adhering to platform UI standards.
 */
export interface IThemedToggleProps extends Omit<TouchableOpacityProps, "style" | "onPress"> {
  /**
   * The current boolean value of the toggle switch.
   */
  value: boolean;

  /**
   * Callback invoked when the toggle state changes.
   */
  onValueChange: (val: boolean) => void;

  /**
   * Optional primary label text displayed next to the switch.
   */
  label?: string;

  /**
   * Optional dynamic label displayed specifically when value is true (overrides label).
   */
  activeLabel?: string;

  /**
   * Optional dynamic label displayed specifically when value is false (overrides label).
   */
  inactiveLabel?: string;

  /**
   * Optional descriptive secondary subtitle or helper text.
   */
  sublabel?: string;

  /**
   * Position of the label relative to the switch control.
   * @default "left"
   */
  labelPosition?: "left" | "right";

  /**
   * Whether the toggle is disabled from user interaction.
   * @default false
   */
  disabled?: boolean;

  /**
   * Custom track colors for inactive and active states.
   */
  trackColor?: {
    false?: string;
    true?: string;
  };

  /**
   * Custom thumb color.
   */
  thumbColor?: string;

  /**
   * Custom container style.
   */
  style?: StyleProp<ViewStyle>;

  /**
   * Custom label text style.
   */
  labelStyle?: StyleProp<TextStyle>;

  /**
   * Custom sublabel text style.
   */
  sublabelStyle?: StyleProp<TextStyle>;

  /**
   * Test identifier for end-to-end tests.
   */
  testID?: string;
}

export type ThemedToggleProps = IThemedToggleProps;

/**
 * Standard ThemedToggle component providing a unified, accessible, and token-bound
 * switch control across the platform. Replaces ad-hoc `styles.switchWrapper` and raw `<Switch>`.
 *
 * @param {IThemedToggleProps} props - Component properties.
 * @returns {React.JSX.Element}
 */
export function ThemedToggle({
  value,
  onValueChange,
  label,
  activeLabel,
  inactiveLabel,
  sublabel,
  labelPosition = "left",
  disabled = false,
  trackColor,
  thumbColor,
  style,
  labelStyle,
  sublabelStyle,
  testID,
  ...rest
}: IThemedToggleProps): React.JSX.Element {
  const tintColor = useThemeColor({}, "tint");
  const textMuted = useThemeColor({}, "textMuted");
  const textColor = useThemeColor({}, "text");

  const resolvedLabel = value
    ? activeLabel ?? label
    : inactiveLabel ?? label;

  const defaultTrackColor = {
    false: "#767577",
    true: tintColor,
  };

  const handlePress = () => {
    if (!disabled) {
      onValueChange(!value);
    }
  };

  const switchElement = (
    <Switch
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
      trackColor={trackColor ?? defaultTrackColor}
      thumbColor={thumbColor}
      accessibilityRole="switch"
      accessibilityState={{ checked: value, disabled }}
      testID={testID ? `${testID}-switch` : undefined}
    />
  );

  const textElement = resolvedLabel || sublabel ? (
    <View style={[styles.textContainer, labelPosition === "right" && styles.textContainerRight]}>
      {resolvedLabel ? (
        <ThemedText
          style={[
            styles.label,
            { color: disabled ? textMuted : textColor },
            value && activeLabel ? { color: tintColor, fontWeight: "700" } : null,
            labelStyle,
          ]}
        >
          {resolvedLabel}
        </ThemedText>
      ) : null}
      {sublabel ? (
        <ThemedText style={[styles.sublabel, { color: textMuted }, sublabelStyle]}>
          {sublabel}
        </ThemedText>
      ) : null}
    </View>
  ) : null;

  return (
    <TouchableOpacity
      activeOpacity={0.7}
      onPress={handlePress}
      disabled={disabled}
      style={[
        styles.wrapper,
        labelPosition === "right" && styles.wrapperRight,
        disabled && styles.disabledWrapper,
        style,
      ]}
      testID={testID}
      {...rest}
    >
      {labelPosition === "left" ? (
        <>
          {textElement}
          {switchElement}
        </>
      ) : (
        <>
          {switchElement}
          {textElement}
        </>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: Spacing.one,
  },
  wrapperRight: {
    justifyContent: "flex-start",
  },
  textContainer: {
    flexShrink: 1,
    marginRight: Spacing.one,
  },
  textContainerRight: {
    marginRight: 0,
    marginLeft: Spacing.one,
  },
  label: {
    fontSize: 12,
    fontWeight: "600",
  },
  sublabel: {
    fontSize: 11,
    marginTop: 1,
    opacity: 0.8,
  },
  disabledWrapper: {
    opacity: 0.6,
  },
});
